import { Complex } from "@/CommonVueCore/src/constants/globalEnums/Complex"
import { ls } from "@/CommonVueCore/src/services/secureLs/secure"

export class ComplexService {

  getDynamicComplexes() {
    try {
      let authStore = ls.get("authStore");
      if (!authStore) return null;

      // Handle cases where SecureLS returns a string (persistedstate stringifies before setting)
      if (typeof authStore === "string") {
        try {
          authStore = JSON.parse(authStore);
        } catch (e) {
          console.error("ComplexService: Failed to parse authStore string", e);
          return null;
        }
      }
      
      // Combine allComplexes and ComplexesByUserName if available
      let allComplexes = authStore.allComplexes || [];
      let userComplexes = authStore.ComplexesByUserName || [];

      // Unwrap Vue-style reactive wrappers if they exist (sometimes happens in storage)
      const unwrap = (list) => {
        if (!Array.isArray(list)) return [];
        return list.map(item => {
          if (item?._custom?.value) return item._custom.value;
          if (item?.value && !item.code && !item.PKey) return item.value; // generic reactive wrapper
          return item;
        });
      };

      allComplexes = unwrap(allComplexes);
      userComplexes = unwrap(userComplexes);
      
      const combined = [...allComplexes];
      
      // Add userComplexes if they aren't already in combined
      userComplexes.forEach(uc => {
        const code = uc.code || uc.PKey;
        if (code !== undefined && code !== null && !combined.some(c => (c.code || c.PKey) == code)) {
          combined.push(uc);
        }
      });

      return combined.length > 0 ? combined : null;
    } catch (error) {
      console.error("ComplexService: Failed to retrieve complexes from store", error);
      return null;
    }
  }

  getInfoByCode(code) {
    if (code === undefined || code === null) return null;
    const numericCode = typeof code === "string" ? parseInt(code, 10) : code;
  
    // 1. Try dynamic store complexes first
    const dynamicComplexes = this.getDynamicComplexes();
    if (dynamicComplexes && Array.isArray(dynamicComplexes)) {
      const found = dynamicComplexes.find(item => (item.code || item.PKey) == numericCode);
      if (found) {
        return {
          ...found,
          description: found.description || found.Name || found.name,
          code: found.code || found.PKey
        };
      }
    }

    // 2. Fallback to static Complex enum
    for (const key of Object.keys(Complex)) {
      const value = Complex[key];
  
      if (Array.isArray(value)) {
        const found = value.find(item => item.code == numericCode);
        if (found) {
          return found;
        }
      } else if (typeof value === "object" && value.code == numericCode) {
        return value;
      }
    }
  
    return null;
  }
  
  getAllCodes() {
    const codes = new Set();

    // 1. Dynamic codes
    const dynamicComplexes = this.getDynamicComplexes();
    if (dynamicComplexes && Array.isArray(dynamicComplexes)) {
      dynamicComplexes.forEach(item => {
        const val = item.code || item.PKey;
        if (val !== undefined && val !== null) codes.add(val);
      });
    }

    // 2. Static codes
    for (const key in Complex) {
      const value = Complex[key];

      if (Array.isArray(value)) {
        for (const item of value) {
          codes.add(item.code);
        }
      } else if (typeof value === "object" && "code" in value) {
        codes.add(value.code);
      }
    }
    return Array.from(codes);
  }

  getInfoByDescription(description) {
    if (!description) return null;

    // 1. Dynamic complexes
    const dynamicComplexes = this.getDynamicComplexes();
    if (dynamicComplexes && Array.isArray(dynamicComplexes)) {
      const found = dynamicComplexes.find(item => (item.description || item.Name || item.name) === description);
      if (found) {
        return {
          ...found,
          description: found.description || found.Name || found.name,
          code: found.code || found.PKey
        };
      }
    }

    // 2. Static complexes
    for (const key in Complex) {
      const value = Complex[key];
      
      if (Array.isArray(value)) {
        for (const item of value) {
          if (item.description === description) {
            return item;
          }
        }
      } else if (typeof value === 'object' && value.description === description) {
        return value;
      }
    }
    
    return null;
  }

  getInfoByDescriptions(descriptions) {
    const results = [];
  
    for (const description of descriptions) {
      const info = this.getInfoByDescription(description);
      if (info) {
        results.push(info);
      }
    }
  
    return results;
  }
}
